import React from 'react';
import MaisAcoes, { ItemDeMenu } from '../MaisAcoes';
import SortIcon from '../SortIcon';
import { fmtCNPJ, fmtCurrency, fmtDate, fmtPrazo, getDaysUntil } from '../../../utils/formatters';
import { SEM_BARRA, TONE_BADGE, TONE_BARRA, TONE_TEXT, type StatusTone, BARRA_DO_SINAL } from '../../../utils/statusColors';
import { tomDoAtraso } from '../../../utils/recebiveis';
import Icon from '../Icon';
import { opcaoAtualForaDaLista } from '../../../design';

/**
 * AS PEÇAS DA LISTA (padrão das listas de Clientes, fase 1, Heitor 21/10/2026: A1 a G1). O guia fixava o contêiner, o
 * cabeçalho, a busca e as cores, mas nunca a LINHA: altura, ID, nome e sub-linha, dinheiro, status, ações, cards do topo
 * e paginação. Foi por esse buraco que Clientes, DDRs, Assinaturas, Reajustes e Grupos se separaram.
 *
 * A1: são PEÇAS que cada tela monta, e não uma tabela única. Assinaturas agrupa e expande, Reajustes tem seções, DDRs tem
 * seleção para o lote, Clientes recolhe filiais: uma tabela genérica viraria uma pilha de exceções. O que é igual mora
 * aqui; o que é de cada tela fica na tela. As regras estão no guia (docs/ui-guidelines.md, §3c) e em regrasDaLinha.ts.
 *
 * Fase 1: as peças nascem e nenhuma tela muda. As fases 2 a 5 convertem uma tela por PR (Clientes e DDRs, Assinaturas,
 * Reajustes, Grupos).
 */

/** B1: a linha COMPACTA, como a de DDRs. Uma só altura em todas as listas. */
export const CELULA = 'px-4 py-3';

/**
 * A casca da lista. ⛔ NUNCA overflow-hidden/auto/scroll: eles fazem da casca uma caixa de rolagem, e o cabeçalho fixo
 * passa a grudar NELA, rolando junto com a página (guia §3.6). A #556 pôs um overflow-hidden aqui para arredondar os
 * cantos e o cabeçalho dos DDRs voltou a rolar (#560).
 *
 * ⭐ `overflow-clip` (22/10/2026, os cantos que o Heitor viu quadrados): sem nada, o fundo do cabeçalho e da última linha
 * pintava um bloco quadrado sobre o canto arredondado. O `clip` recorta no raio SEM criar caixa de rolagem. Medido no
 * HUB, rolando a página: com clip o cabeçalho ficou no topo do <main> (64), igual a sem nada; com hidden foi a -1471. E o
 * menu ⋯ da linha, que é `fixed`, escapa do recorte (sonda fixed fora da casca: pintada; a mesma em absolute: recortada).
 */
export function CascaDaLista({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-central-cartao rounded-central border border-central-borda overflow-clip ${className}`} data-peca='casca-da-lista'>
      {children}
    </div>
  );
}

/** A tabela: `table-fixed` sempre (guia §3.8), para os `w-*` do cabeçalho valerem. */
export function TabelaDaLista({ children }: { children: React.ReactNode }) {
  return <table className='w-full table-fixed' data-peca='tabela-da-lista'>{children}</table>;
}

/**
 * O cabeçalho fixo. -top-6 desconta o padding de 24px do <main> que rola (cabecalhoFixo.test): com top-0 a linha rolada
 * aparecia acima dele. O fundo é o da Central, como o de Clientes (fase 2, 22/10/2026: o de DDRs era slate-900).
 *
 * `topo` (px) é para a tela que tem OUTRA faixa fixa em cima da lista (a barra de seleção das Faturas, 22/10/2026): sem
 * ele, a barra cobria o cabeçalho. A tela mede a faixa e passa onde o cabeçalho gruda.
 */
export function CabecalhoDaLista({ children, topo }: { children: React.ReactNode; topo?: number }) {
  return (
    <thead className='sticky -top-6 z-20 bg-central-fundo/95 backdrop-blur border-b border-central-borda' style={topo !== undefined ? { top: topo } : undefined} data-peca='cabecalho-da-lista'>
      <tr>{children}</tr>
    </thead>
  );
}

type Alinhamento = 'esquerda' | 'centro' | 'direita';
const ALINHA: Record<Alinhamento, string> = { esquerda: 'text-left', centro: 'text-center', direita: 'text-right' };

/**
 * Uma coluna do cabeçalho: rótulo em maiúsculas, a largura (`w-*`) quando a coluna não é a principal, e a ordenação
 * quando há. A coluna ordenada é ESTADO: peso e seta (SortIcon), não cor; o `aria-sort` diz a ordem ao leitor de tela.
 */
export function ColunaDaLista({ rotulo, largura = '', alinhamento = 'esquerda', ordem, onOrdenar }: {
  rotulo: React.ReactNode;
  largura?: string;
  alinhamento?: Alinhamento;
  /** `false` = ordenável e não ordenada; ausente = não ordena. */
  ordem?: 'asc' | 'desc' | false;
  onOrdenar?: () => void;
}) {
  const ordena = ordem !== undefined && !!onOrdenar;
  return (
    <th
      className={`${CELULA} text-xs font-semibold uppercase ${ordem ? 'text-white' : 'text-slate-400'} ${ordena ? 'cursor-pointer hover:text-white select-none' : ''} ${ALINHA[alinhamento]} ${largura}`}
      onClick={ordena ? onOrdenar : undefined}
      aria-sort={ordem ? (ordem === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      {rotulo}
      {ordena && <SortIcon dir={ordem as 'asc' | 'desc' | false} />}
    </th>
  );
}

/**
 * Uma linha. O estado que pede atenção leva a barra de 3px na primeira célula (`barra`, por tom); o resto é sem barra,
 * com a borda transparente na base para o texto não pular. `apagada` é o inativo: a linha inteira esmaece.
 */
export function LinhaDaLista({ children, apagada = false, destacada = false, onClick, className = '', ...resto }: {
  children: React.ReactNode;
  apagada?: boolean;
  /** A linha aberta (expandida) ou filha de um grupo aberto: o fundo elevado diz que o que vem embaixo é dela. */
  destacada?: boolean;
  onClick?: () => void;
} & React.HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr className={`hover:bg-central-elevado ${destacada ? 'bg-central-elevado/60' : ''} ${apagada ? 'opacity-60' : ''} ${onClick ? 'cursor-pointer' : ''} ${className}`} onClick={onClick} {...resto}>
      {children}
    </tr>
  );
}

/** A barra de estado da primeira célula: âmbar ou vermelha só no que pede ação (TONE_BARRA); o resto, transparente. */
export const barraDoEstado = (tom?: StatusTone | null): string => `border-l-[3px] ${(tom && TONE_BARRA[tom]) || SEM_BARRA}`;

/**
 * A barra pelo SINAL (ver BARRA_DO_SINAL): verde no positivo, vermelha no negativo, transparente no zero. Para a 1ª célula
 * da linha. Num cartão, use `contornoDoSinal`, que não põe a borda transparente (ela apagaria a borda esquerda do cartão).
 */
export const barraDoSinal = (v: number): string =>
  `border-l-[3px] ${v > 0 ? BARRA_DO_SINAL.positivo : v < 0 ? BARRA_DO_SINAL.negativo : SEM_BARRA}`;
export const contornoDoSinal = (v: number): string => (v > 0 || v < 0 ? barraDoSinal(v) : '');
/** Num cartão de saldo que quase sempre é positivo (a conta): a barra só no negativo. */
export const contornoSoNegativo = (v: number): string => (v < 0 ? barraDoEstado('danger') : '');
/**
 * O contorno do SALDO DE CONTA (Contas e os cartões das contas no Fluxo de Caixa), decisão C do Heitor na #584: C1 = só no
 * negativo; C2 = pelo sinal, verde e vermelho. Um lugar só, para as duas telas não discordarem.
 */
const CONTORNO_DA_CONTA = 'so-negativo' as 'so-negativo' | 'sinal';
export const contornoDoSaldoDeConta = (v: number): string => (CONTORNO_DA_CONTA === 'sinal' ? contornoDoSinal(v) : contornoSoNegativo(v));

/** O ID: cinza, mono, SEM pílula e sem "#" (era "#58" em pílula em Assinaturas). */
export function CelulaId({ id, barra, antes, recuo = false }: {
  id: React.ReactNode;
  barra?: StatusTone | null;
  /** Antes do ID, na mesma linha: a seta que abre a linha (Assinaturas). */
  antes?: React.ReactNode;
  /** Linha filha (a assinatura dentro do cliente aberto): recua sob a de cima. */
  recuo?: boolean;
}) {
  return (
    <td className={`${CELULA} ${barraDoEstado(barra)} ${recuo ? 'pl-10' : ''}`} data-peca='celula-id'>
      <div className='flex items-center gap-2'>
        {antes}
        <span className='text-xs text-slate-500 font-mono'>{id}</span>
      </div>
    </td>
  );
}

/**
 * As partes da sub-linha C1, na ordem: razão · CNPJ · cidade · o que a tela somar. Exportada para quem mostra o cliente
 * fora de uma <td> (os cartões de Grupos, fase 5): a ordem e a máscara do CNPJ ficam num lugar só.
 */
export function partesDaSubLinha({ razao, cnpj, cidade, outras = [] }: {
  razao?: string | null;
  cnpj?: string | null;
  cidade?: string | null;
  outras?: (string | null | undefined)[];
}): string[] {
  return [razao, cnpj ? fmtCNPJ(cnpj) : null, cidade, ...outras].filter((p): p is string => !!p && String(p).trim() !== '');
}

/**
 * O cliente: o nome em cima e UMA sub-linha com o resto (C1: razão social · CNPJ · cidade), nunca colunas próprias
 * (guia §3.3). O nome trunca com o title; a coluna não tem largura fixa, herda a sobra (guia §3.2).
 */
export function CelulaCliente({ nome, razao, cnpj, cidade, outras = [], extra, antes, selos, recuo = false, detalhe }: {
  nome: React.ReactNode;
  razao?: string | null;
  cnpj?: string | null;
  cidade?: string | null;
  /** O que a tela soma à MESMA sub-linha, depois da cidade (Reajustes: a assinatura e o nº do contrato). */
  outras?: (string | null | undefined)[];
  /** Uma linha a mais, só quando é da tela (o "→ cliente final" dos DDRs). */
  extra?: React.ReactNode;
  /** Antes do nome, na mesma linha: a seta que recolhe as empresas do grupo (Clientes). */
  antes?: React.ReactNode;
  /** Depois do nome, na mesma linha: os selos da tela ("3 no grupo", "do grupo", "Pub."). */
  selos?: React.ReactNode;
  /** Empresa do grupo: o nome recua sob a matriz. */
  recuo?: boolean;
  /** O que não cabe na sub-linha e vale ver no tooltip dela (a origem do cliente, a IE). */
  detalhe?: string | null;
}) {
  const partes = partesDaSubLinha({ razao, cnpj, cidade, outras });
  const titulo = typeof nome === 'string' ? nome : undefined;
  const tituloDaSub = [partes.join(' · '), detalhe].filter(Boolean).join('\n');
  return (
    <td className={`${CELULA} min-w-0`} data-peca='celula-cliente'>
      <div className='flex items-center gap-2 min-w-0'>
        {antes}
        <p className={`font-medium text-white text-sm truncate ${recuo ? 'pl-4' : ''}`} title={titulo}>{nome}</p>
        {selos}
      </div>
      {partes.length > 0 && <p className='text-[11px] text-slate-500 truncate' title={tituloDaSub} data-sub-linha>{partes.join(' · ')}</p>}
      {extra}
    </td>
  );
}

/** E1: o dinheiro é BRANCO com peso, alinhado à direita, em algarismos tabulares. Sem valor, "—". Verde saiu. */
export function CelulaDinheiro({ valor, children, title }: {
  valor: number | string | null | undefined;
  /** Linhas embaixo do valor, quando são da tela (a forma de pagamento e o total do grupo, em Assinaturas). */
  children?: React.ReactNode;
  title?: string;
}) {
  const vazio = valor === null || valor === undefined || valor === '';
  return (
    <td className={`${CELULA} text-right whitespace-nowrap`} data-peca='celula-dinheiro' title={title}>
      {vazio ? <span className='text-slate-500'>—</span> : <span className='text-sm font-semibold text-central-texto tabular-nums'>{fmtCurrency(valor as number | string)}</span>}
      {children}
    </td>
  );
}

/**
 * O tom de um PRAZO (vencimento), o mesmo da coluna Situação de Recebíveis (bloco E, 12/10/2026): vencido até 7 dias em
 * âmbar, depois vermelho; vence hoje, a vencer, sem data ou já quitado não têm tom. É ESTA função que a barra da linha
 * (barraDoEstado) e a CelulaPrazo usam, para as duas nunca discordarem.
 */
export function tomDoPrazo(vencimento: string | null | undefined, quitado = false): Extract<StatusTone, 'warning' | 'danger'> | null {
  if (quitado || !vencimento) return null;
  const dias = getDaysUntil(vencimento);
  return dias === null ? null : tomDoAtraso(-dias);
}

/**
 * O PRAZO da linha (22/10/2026, pedido do Heitor: a coluna Situação de Recebíveis vira peça, para Faturas, Boletos,
 * Despesas, Op. de Crédito e onde houver vencimento). Estado é peso + ícone, não pílula pintada:
 * - vencido: o ícone de alerta no tom do atraso e o prazo em negrito ("vencido há 12 dias");
 * - a vencer: o prazo num selo NEUTRO ("em 3 dias", "vence hoje");
 * - quitado: o rótulo do quitado ("Paga") neutro;
 * - sem vencimento: "sem vencimento", neutro.
 * `children` vai embaixo (o "Parcial" de Recebíveis).
 */
export function CelulaPrazo({ vencimento, quitado = false, rotuloQuitado = 'Paga', mostrarData = false, title, children }: {
  vencimento: string | null | undefined;
  quitado?: boolean;
  /** `null`: o quitado não ganha rótulo (Boletos: a coluna Status já diz "Pago"). */
  rotuloQuitado?: string | null;
  /** A data do vencimento em cima do prazo (Boletos: a coluna é "Vencimento", não "Situação"). */
  mostrarData?: boolean;
  title?: string;
  children?: React.ReactNode;
}) {
  const tom = tomDoPrazo(vencimento, quitado);
  const dias = vencimento ? getDaysUntil(vencimento) : null;
  return (
    <td className={`${CELULA} whitespace-nowrap`} title={title} data-peca='celula-prazo' data-prazo={quitado ? 'quitado' : tom || (vencimento ? 'em-dia' : 'sem-vencimento')}>
      <div className='flex flex-col items-start gap-1'>
        {mostrarData && <span className='text-sm text-slate-300' data-data-do-prazo>{vencimento ? fmtDate(vencimento) : '—'}</span>}
        {quitado ? (
          rotuloQuitado === null ? null : <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${TONE_BADGE.neutral}`}>{rotuloQuitado}</span>
        ) : tom ? (
          <span className='inline-flex items-center gap-1 text-xs text-slate-100 whitespace-nowrap'>
            <Icon name='alerta' className={`w-4 h-4 shrink-0 ${TONE_TEXT[tom]}`} />
            <b className='font-semibold'>{fmtPrazo(dias)}</b>
          </span>
        ) : (
          <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${TONE_BADGE.neutral}`}>
            {vencimento ? fmtPrazo(dias) : 'sem vencimento'}
          </span>
        )}
        {children}
      </div>
    </td>
  );
}

/**
 * O cabeçalho de uma tabela INTERNA (o detalhe que abre dentro da linha: os locais e os itens da assinatura). Não é
 * fixo, porque não é a lista: rola junto com a linha que o abriu. O tom é o elevado da Central.
 */
export function CabecalhoInterno({ children, fixoNaCaixa = false }: {
  children: React.ReactNode;
  /**
   * A tabela interna que rola na PRÓPRIA caixa (`max-h-64 overflow-y-auto`, as da ficha de Op. de Crédito): o cabeçalho
   * gruda no topo dessa caixa (`sticky top-0`). Não é o cabeçalho da lista, que gruda no <main> (-top-6).
   */
  fixoNaCaixa?: boolean;
}) {
  return <thead className={`bg-central-elevado/80 ${fixoNaCaixa ? 'sticky top-0' : ''}`} data-peca='cabecalho-interno'>{children}</thead>;
}

/**
 * E1: o status pelo mapa (statusColors). O normal ("Ativo") é NEUTRO; a cor fica para o que pede atenção, e quem diz o
 * tom é a tela (pelo domínio dela), nunca uma cor escrita aqui.
 */
export function CelulaStatus({ texto, tom = 'neutral' }: { texto: React.ReactNode; tom?: StatusTone }) {
  return (
    <td className={`${CELULA} text-center`} data-peca='celula-status'>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${TONE_BADGE[tom]}`}>{texto}</span>
    </td>
  );
}

/**
 * D1: as ações da linha são o editar (ou o VERBO PRINCIPAL da tela, à vista no lugar dele: "Registrar apostila" em
 * Reajustes) e o ⋯. A lixeira mora SEMPRE no ⋯, em perigo (danger), e quem confirma é a tela.
 */
export function CelulaAcoes({ rotuloDoMenu, onEditar, verbo, itens, Icon, antes }: {
  /** Ex.: "Mais ações do DDR 4532250210": o title do ⋯ diz de quem é. */
  rotuloDoMenu: string;
  /**
   * Ação de uso diário que a tela quer à vista ALÉM do verbo, antes dele (o olho de Faturas, pedido do Heitor em 22/10/2026:
   * "usamos bastante"). Exceção da tela, não padrão: o resto continua no ⋯.
   */
  antes?: React.ReactNode;
  onEditar?: () => void;
  /** O verbo principal, quando a tela tem um; aparece no lugar do editar. */
  verbo?: { rotulo: string; onClick: () => void; desabilitado?: boolean; title?: string };
  itens: ItemDeMenu[];
  Icon?: React.FC<{ name: string; className?: string }>;
}) {
  return (
    // O clique nas ações não chega à linha: em Assinaturas a linha inteira abre o detalhe.
    <td className={CELULA} data-peca='celula-acoes' onClick={(e) => e.stopPropagation()}>
      <div className='flex items-center justify-end gap-1'>
        {antes}
        {verbo ? (
          <button type='button' onClick={verbo.onClick} disabled={verbo.desabilitado} title={verbo.title} className='px-2.5 py-1 rounded-lg text-xs font-medium bg-transparent border border-central-borda text-central-texto hover:bg-central-elevado disabled:opacity-50 whitespace-nowrap'>
            {verbo.rotulo}
          </button>
        ) : onEditar ? (
          <button type='button' onClick={onEditar} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Editar' aria-label='Editar'>
            {Icon ? <Icon name='edit' className='w-4 h-4' /> : '✎'}
          </button>
        ) : null}
        <MaisAcoes title={rotuloDoMenu} ancoragem='fixa' itens={itens} />
      </div>
    </td>
  );
}

/**
 * Um card do topo, no molde da tela Clientes: superfície da Central, rótulo em cima, número embaixo. Clicável = filtro, e
 * o ativo é peso + barra de 3px em primary sobre o elevado (nunca o card pintado inteiro).
 */
export function CartaoDoTopo({ rotulo, valor, sub, ativo = false, barra, onClick, className = '', ...resto }: {
  rotulo: React.ReactNode;
  valor: React.ReactNode;
  sub?: React.ReactNode;
  ativo?: boolean;
  /**
   * O cartão que pede atenção (Vencidas em Recebíveis, 22/10/2026): a barra de 3px no tom, como a da linha. O ativo vence,
   * porque o escolhido é o estado que o operador acabou de pôr.
   */
  barra?: StatusTone | null;
  onClick?: () => void;
} & React.HTMLAttributes<HTMLElement>) {
  const marca = ativo ? 'border-l-[3px] border-l-primary bg-central-elevado' : `${barra ? barraDoEstado(barra) : ''} ${onClick ? 'hover:bg-central-elevado' : ''}`;
  const cls = `bg-central-cartao rounded-central p-4 border border-central-borda text-left transition-all ${marca} ${className}`;
  const corpo = (
    <>
      <p className='text-sm text-slate-400'>{rotulo}</p>
      <p className='text-2xl font-bold whitespace-nowrap text-central-texto mt-1 tabular-nums'>{valor}</p>
      {sub && <p className='text-slate-500 text-xs mt-1'>{sub}</p>}
    </>
  );
  return onClick
    ? <button type='button' onClick={onClick} aria-pressed={ativo} className={cls} data-peca='cartao-do-topo' {...resto}>{corpo}</button>
    : <div className={cls} data-peca='cartao-do-topo' {...resto}>{corpo}</div>;
}

/** Os números de página mostrados (até 5, com a atual no meio quando dá). */
export function paginasVisiveis(atual: number, total: number, max = 5): number[] {
  if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
  const meio = Math.floor(max / 2);
  const inicio = Math.min(Math.max(1, atual - meio), total - max + 1);
  return Array.from({ length: max }, (_, i) => inicio + i);
}

/**
 * A paginação, no molde da de DDRs: "Mostrando a–b de N", o tamanho da página e os números. A página atual é estado: peso
 * e o chip slate-600, não cor.
 */
export function PaginacaoDaLista({ pagina, tamanho, total, onPagina, onTamanho, tamanhos = [50, 100, 200], todos = false }: {
  /** 1 = primeira. */
  pagina: number;
  tamanho: number;
  total: number;
  onPagina: (p: number) => void;
  onTamanho?: (t: number) => void;
  tamanhos?: number[];
  /** Oferece "Todos" (a página do tamanho do total), como Clientes e DDRs tinham. */
  todos?: boolean;
}) {
  const ehTodos = todos && !tamanhos.includes(tamanho) && tamanho >= total;
  const totalPaginas = Math.max(1, Math.ceil(total / Math.max(1, tamanho)));
  const inicio = total === 0 ? 0 : (pagina - 1) * tamanho + 1;
  const fim = Math.min(pagina * tamanho, total);
  const botao = 'px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 text-slate-400 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed';
  return (
    <div className='p-4 flex items-center justify-between gap-3 flex-wrap border-t border-central-borda' data-peca='paginacao-da-lista'>
      <div className='flex items-center gap-3'>
        <span className='text-slate-500 text-sm'>Mostrando {inicio}–{fim} de {total}</span>
        {onTamanho && (
          <select aria-label='Linhas por página' value={ehTodos ? 'todos' : tamanho} onChange={(e) => onTamanho(e.target.value === 'todos' ? Math.max(total, 1) : Number(e.target.value))} className='bg-central-elevado border border-central-borda text-central-texto text-sm rounded-lg px-2 py-1'>
            {/* Um tamanho fora da lista (que não seja o "Todos") aparece mesmo assim. */}
            {!ehTodos && opcaoAtualForaDaLista(tamanhos.map((t) => ({ id: t })), tamanho, String(tamanho), false)}
            {tamanhos.map((t) => <option key={t} value={t}>{t}</option>)}
            {todos && <option value='todos'>Todos</option>}
          </select>
        )}
      </div>
      {totalPaginas > 1 && (
        <div className='flex items-center gap-2'>
          {/* Primeira e Última: os DDRs passam de 100 páginas (5.863 ativos, 50 por página). */}
          <button type='button' onClick={() => onPagina(1)} disabled={pagina <= 1} className={botao}>Primeira</button>
          <button type='button' onClick={() => onPagina(pagina - 1)} disabled={pagina <= 1} className={botao}>Anterior</button>
          <div className='flex items-center gap-1'>
            {paginasVisiveis(pagina, totalPaginas).map((p) => (
              <button key={p} type='button' onClick={() => onPagina(p)} aria-current={p === pagina ? 'page' : undefined}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${p === pagina ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                {p}
              </button>
            ))}
          </div>
          <button type='button' onClick={() => onPagina(pagina + 1)} disabled={pagina >= totalPaginas} className={botao}>Próxima</button>
          <button type='button' onClick={() => onPagina(totalPaginas)} disabled={pagina >= totalPaginas} className={botao}>Última</button>
        </div>
      )}
    </div>
  );
}
